import { useState, useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import { Link } from "react-router-dom";
import {
  Target,
  Globe,
  Award,
  TrendingUp,
  Shield,
  Clock,
  Package,
  ArrowRight,
  Rocket,
  Heart,
  Star,
  Send,
  Mail,
  Phone,
  Compass,
  Building2,
  Headphones,
  ThumbsUp,
  Truck,
  Box,
  Quote,
  Info,
  Handshake,
} from "lucide-react";

const About = () => {
  const { scrollYProgress } = useScroll();
  const [hoveredCard, setHoveredCard] = useState(null);
  const [activeTab, setActiveTab] = useState("mission");

  // Refs for each section
  const missionRef = useRef(null);
  const servicesRef = useRef(null);
  const valuesRef = useRef(null);
  const impactRef = useRef(null);

  const services = [
    {
      icon: Truck,
      title: "Mixed Containers",
      description:
        "Flexible consolidation of multiple products in one shipment",
      gradient: "from-blue-500 to-cyan-500",
      detail:
        "Combine different products from various suppliers into a single container, optimizing costs and logistics.",
    },
    {
      icon: Box,
      title: "LCL & FCL",
      description: "Less-than-container and full-container load options",
      gradient: "from-purple-500 to-pink-500",
      detail:
        "Choose between shared container space or dedicated full containers based on your volume.",
    },
    {
      icon: Handshake,
      title: "Matchmaking",
      description: "Connect buyers with the right suppliers worldwide",
      gradient: "from-orange-500 to-red-500",
      detail:
        "We match your sourcing needs with verified manufacturers and suppliers for reliable, long-term partnerships.",
    },
    {
      icon: Building2,
      title: "Export Development",
      description: "Strategic support for manufacturers entering new markets",
      gradient: "from-green-500 to-emerald-500",
      detail:
        "End-to-end export development programs for sustainable international growth.",
    },
  ];

  const stats = [
    {
      value: "98%",
      label: "Client Satisfaction",
      icon: ThumbsUp,
      trend: "Excellent rating",
    },
    {
      value: "+10",
      label: "Countries Served",
      icon: Globe,
      trend: "5 new markets",
    },
    {
      value: "24/7",
      label: "Support Available",
      icon: Headphones,
      trend: "Rapid response",
    },
  ];

  const values = [
    {
      icon: Shield,
      title: "Transparency First",
      description:
        "No hidden costs, complete visibility throughout the process.",
      color: "#3B82F6",
    },
    {
      icon: Heart,
      title: "Partnership Focus",
      description: "Building long-term relationships, not just transactions.",
      color: "#EC4899",
    },
    {
      icon: Rocket,
      title: "Innovation Driven",
      description: "Leveraging technology for efficient global trade.",
      color: "#10B981",
    },
  ];

  const testimonials = [
    {
      quote:
        "Trade Bridge has transformed our supply chain. Their flexible shipping solutions and dedicated support are exceptional.",
      author: "Sarah Johnson",
      role: "Supply Chain Director",
      company: "Global Retail Co.",
      rating: 5,
    },
    {
      quote:
        "The export development program helped us enter 3 new markets within 6 months. Truly game-changing partnership.",
      author: "Mohamed Ahmed",
      role: "CEO",
      company: "Egyptian Foods",
      rating: 5,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 border border-gray-200 dark:border-gray-900 rounded-2xl shadow-lg">
      {/* Blur Background Layers */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[40%] -left-[20%] w-[80%] h-[80%] bg-gradient-to-br from-primary-200/20 to-secondary-200/20 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-[40%] -right-[20%] w-[80%] h-[80%] bg-gradient-to-tl from-primary-300/10 to-secondary-300/10 dark:from-primary-800/10 dark:to-secondary-800/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] lg:min-h-[80vh] flex items-center justify-center pt-20 lg:pt-0">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-full border border-white/30 dark:border-gray-800/30 shadow-sm mb-4 sm:mb-6">
                <Info className="w-3 h-3 sm:w-4 sm:h-4 text-primary-500" />
                <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                  Egypt's Premier Sourcing Partner
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight mb-4 sm:mb-6">
                <span className="text-gray-900 dark:text-white">
                  Global Trade,
                </span>
                <br />
                <span className="bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-600 bg-clip-text text-transparent">
                  Simplified.
                </span>
              </h1>

              <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-6 sm:mb-8 px-4">
                Trade Bridge connects global buyers with trusted FMCGs
                suppliers, offering flexible shipping solutions and export
                development expertise.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-medium rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  <span>Start Sourcing</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/products"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl text-gray-700 dark:text-gray-300 font-medium rounded-xl sm:rounded-2xl border border-white/50 dark:border-gray-800/50 hover:bg-white/60 dark:hover:bg-gray-900/60 transition-all"
                >
                  Explore Products
                </Link>
              </div>
            </motion.div>

            {/* Stats Row */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.7,
                delay: 0.2,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="mt-12 sm:mt-16 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 max-w-4xl mx-auto px-4"
            >
              {stats.map((stat, i) => {
                const Icon = stat.icon;
                return (
                  <div
                    key={i}
                    className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-white/30 dark:border-gray-800/30"
                  >
                    <div className="flex items-center gap-2 sm:gap-3 mb-2">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      </div>
                      <div className="text-left min-w-0">
                        <div className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                          {stat.value}
                        </div>
                        <div className="text-[10px] sm:text-xs text-gray-500 truncate">
                          {stat.label}
                        </div>
                      </div>
                    </div>
                    <div className="text-[8px] sm:text-[9px] text-green-600 dark:text-green-400">
                      {stat.trend}
                    </div>
                  </div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tab Bar */}
      <div className="relative container mx-auto px-4 mt-4 sm:mt-8 z-40 transition-all duration-300">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-xl sm:rounded-2xl p-1 border border-white/30 dark:border-gray-800/30 shadow-sm overflow-x-auto">
            <div className="flex gap-1 min-w-max sm:min-w-0">
              {[
                {
                  id: "mission",
                  label: "Mission",
                  icon: Target,
                  ref: missionRef,
                },
                {
                  id: "services",
                  label: "Services",
                  icon: Package,
                  ref: servicesRef,
                },
                { id: "values", label: "Values", icon: Heart, ref: valuesRef },
                {
                  id: "impact",
                  label: "Impact",
                  icon: TrendingUp,
                  ref: impactRef,
                },
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl transition-all text-xs sm:text-sm ${
                      activeTab === tab.id
                        ? "bg-white dark:bg-gray-800 shadow-md text-primary-600 dark:text-primary-400"
                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="font-medium hidden sm:inline">
                      {tab.label}
                    </span>
                    <span className="font-medium sm:hidden">
                      {tab.label.slice(0, 3)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Dynamic Content based on Active Tab */}
      <AnimatePresence mode="wait">
        {activeTab === "mission" && (
          <motion.section
            key="mission"
            ref={missionRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="py-8 sm:py-12 lg:py-16"
          >
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-4xl mx-auto">
                <div className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl sm:rounded-3xl border border-white/30 dark:border-gray-800/30 shadow-xl overflow-hidden">
                  <div className="p-5 sm:p-6 md:p-8 lg:p-10">
                    <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center flex-shrink-0">
                        <Compass className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                          Our Mission
                        </h2>
                        <p className="text-xs sm:text-sm text-gray-500">
                          Bridging gaps, creating opportunities
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4 sm:space-y-6">
                      <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                        Trade Bridge is a sourcing and match making company and
                        export facilitation company based in Egypt, connecting
                        global buyers with trusted FMCGs suppliers from Egypt
                        and other international markets.
                      </p>

                      <div className="bg-gradient-to-r from-primary-50/50 to-secondary-50/50 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-primary-100 dark:border-primary-800/30">
                        <p className="text-sm sm:text-base text-gray-800 dark:text-gray-200 font-medium leading-relaxed">
                          "We offer flexible shipment solutions, including mixed
                          containers, LCLs, FCLs, and air shipments tailored to
                          our clients' needs."
                        </p>
                      </div>

                      <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                        In addition, we support manufacturers through export
                        development projects, helping companies enter new
                        markets and build sustainable international growth.
                      </p>

                      <div className="bg-white/60 dark:bg-gray-900/60 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center border border-white/40 dark:border-gray-800/40">
                        <p className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
                          Trade Bridge ensures flexibility, efficiency, and
                          competitive value.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.section>
        )}

        {activeTab === "services" && (
          <motion.section
            key="services"
            ref={servicesRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="py-8 sm:py-12 lg:py-16"
          >
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-6xl mx-auto">
                {services.map((service, i) => {
                  const Icon = service.icon;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.1 }}
                      onHoverStart={() => setHoveredCard(i)}
                      onHoverEnd={() => setHoveredCard(null)}
                      className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/30 dark:border-gray-800/30 shadow-lg overflow-hidden"
                    >
                      <div className="p-4 sm:p-5 md:p-6">
                        <div
                          className={`w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-gradient-to-r ${service.gradient} flex items-center justify-center mb-3 sm:mb-4 shadow-lg`}
                        >
                          <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                        </div>
                        <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white mb-1 sm:mb-2">
                          {service.title}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2 sm:mb-3">
                          {service.description}
                        </p>
                        <AnimatePresence>
                          {hoveredCard === i && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-200 dark:border-gray-700"
                            >
                              <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                                {service.detail}
                              </p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.section>
        )}

        {activeTab === "values" && (
          <motion.section
            key="values"
            ref={valuesRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="py-8 sm:py-12 lg:py-16"
          >
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto">
                {values.map((value, i) => {
                  const Icon = value.icon;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 text-center border border-white/30 dark:border-gray-800/30 hover:shadow-xl transition-all group"
                    >
                      <div
                        className="w-12 h-12 sm:w-14 sm:h-14 mx-auto rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-4 transition-all group-hover:scale-110"
                        style={{
                          background: `linear-gradient(135deg, ${value.color}20, ${value.color}40)`,
                        }}
                      >
                        <Icon
                          className="w-6 h-6 sm:w-7 sm:h-7"
                          style={{ color: value.color }}
                        />
                      </div>
                      <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white mb-1 sm:mb-2">
                        {value.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        {value.description}
                      </p>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.section>
        )}

        {activeTab === "impact" && (
          <motion.section
            key="impact"
            ref={impactRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="py-8 sm:py-12 lg:py-16"
          >
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-5xl mx-auto space-y-4 sm:space-y-6">
                {/* Global Presence */}
                <div className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 border border-white/30 dark:border-gray-800/30">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <Globe className="w-5 h-5 sm:w-6 sm:h-6 text-primary-500" />
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                      Global Footprint
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {[
                      {
                        region: "North America",
                        percentage: 35,
                        color: "from-blue-500",
                      },
                      {
                        region: "Europe",
                        percentage: 30,
                        color: "from-purple-500",
                      },
                      {
                        region: "Middle East",
                        percentage: 20,
                        color: "from-orange-500",
                      },
                      {
                        region: "Asia Pacific",
                        percentage: 15,
                        color: "from-green-500",
                      },
                    ].map((market, i) => (
                      <div key={i}>
                        <div className="flex justify-between text-xs sm:text-sm mb-1">
                          <span className="text-gray-700 dark:text-gray-300">
                            {market.region}
                          </span>
                          <span className="text-primary-600 font-semibold">
                            {market.percentage}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 sm:h-2">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${market.percentage}%` }}
                            transition={{ duration: 1, delay: i * 0.1 }}
                            className={`bg-gradient-to-r ${market.color} to-secondary-500 h-1.5 sm:h-2 rounded-full`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Key Achievements - Responsive Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {[
                    {
                      icon: Award,
                      value: "98%",
                      label: "Client Retention",
                      desc: "Long-term partnerships",
                    },
                    {
                      icon: Clock,
                      value: "<24h",
                      label: "Response Time",
                      desc: "Rapid support",
                    },
                    {
                      icon: TrendingUp,
                      value: "150%",
                      label: "Growth Rate",
                      desc: "Year over year",
                    },
                  ].map((achievement, i) => {
                    const Icon = achievement.icon;
                    return (
                      <div
                        key={i}
                        className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 text-center border border-white/30 dark:border-gray-800/30"
                      >
                        <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto rounded-lg sm:rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center mb-2 sm:mb-3">
                          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                        </div>
                        <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                          {achievement.value}
                        </div>
                        <div className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mt-1">
                          {achievement.label}
                        </div>
                        <div className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
                          {achievement.desc}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Export Development Highlight */}
                <div className="bg-gradient-to-r from-primary-500/10 to-secondary-500/10 backdrop-blur-xl rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 border border-primary-200/30 dark:border-primary-800/30">
                  <div className="text-center">
                    <Building2 className="w-10 h-10 sm:w-12 sm:h-12 text-primary-500 mx-auto mb-3 sm:mb-4" />
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-1 sm:mb-2">
                      Export Development Projects
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">
                      Helping manufacturers enter new markets and build
                      sustainable international growth
                    </p>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 text-sm sm:text-base text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Learn More <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>

                {/* Testimonials Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {testimonials.map((testimonial, i) => (
                    <div
                      key={i}
                      className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 border border-white/30 dark:border-gray-800/30"
                    >
                      <Quote className="w-6 h-6 sm:w-8 sm:h-8 text-primary-400 mb-3 sm:mb-4" />
                      <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 italic mb-3 sm:mb-4">
                        "{testimonial.quote}"
                      </p>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
                          {testimonial.author}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-500">
                          {testimonial.role}, {testimonial.company}
                        </p>
                        <div className="flex gap-0.5 mt-2">
                          {[...Array(testimonial.rating)].map((_, j) => (
                            <Star
                              key={j}
                              className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400 fill-current"
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-600 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
              <div className="relative p-6 sm:p-8 md:p-10 lg:p-12 text-center text-white">
                <div className="absolute inset-0 bg-black/20" />
                <div className="relative">
                  <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">
                    Ready to Transform?
                  </h2>
                  <p className="text-sm sm:text-base text-white/90 mb-6 sm:mb-8 max-w-md mx-auto px-4">
                    Join global businesses that trust Trade Bridge for their
                    sourcing needs.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
                    <Link
                      to="/contact"
                      className="inline-flex items-center justify-center gap-2 px-6 py-2.5 sm:px-8 sm:py-3 bg-white text-primary-600 rounded-xl sm:rounded-2xl font-semibold hover:shadow-xl transition-all hover:scale-105 text-sm sm:text-base"
                    >
                      <Send className="w-4 h-4" />
                      Contact Sales
                    </Link>
                    <Link
                      to="/inquiry"
                      className="inline-flex items-center justify-center gap-2 px-6 py-2.5 sm:px-8 sm:py-3 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl font-semibold hover:bg-white/30 transition-all border border-white/30 text-sm sm:text-base"
                    >
                      Request Quote
                    </Link>
                  </div>

                  <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-xs sm:text-sm">
                    <div className="flex items-center gap-2">
                      <Phone className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span>+20-111-477-9577</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span>info@tradebridge-export.com</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
